import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  useMedicalDrugs,
  useCreateMedicalDrug,
  useUpdateMedicalDrug,
  useDeleteMedicalDrug,
  type MedicalDrug,
} from "@/hooks/api/useMedicalDrugs";
import { Pencil, Trash2, Plus, Search } from "lucide-react";

type DrugForm = { name: string; nameKz: string; inn: string; manufacturer: string };

const emptyForm: DrugForm = { name: "", nameKz: "", inn: "", manufacturer: "" };

function DrugFormDialog({
  open,
  onClose,
  initial,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  initial?: DrugForm;
  onSubmit: (data: DrugForm) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<DrugForm>(initial ?? emptyForm);

  useEffect(() => {
    if (open) setForm(initial ?? emptyForm);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof DrugForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? t("drugs.editDrug") : t("drugs.addDrug")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("drugs.name")} *</label>
            <Input value={form.name} onChange={set("name")} required placeholder={t("drugs.namePlaceholder")} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("drugs.nameKz")}</label>
            <Input value={form.nameKz} onChange={set("nameKz")} placeholder={t("drugs.nameKzPlaceholder")} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("drugs.inn")} *</label>
            <Input value={form.inn} onChange={set("inn")} required placeholder={t("drugs.innPlaceholder")} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("drugs.manufacturer")}</label>
            <Input value={form.manufacturer} onChange={set("manufacturer")} placeholder={t("drugs.manufacturerPlaceholder")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={!form.name.trim() || !form.inn.trim() || isPending}>
              {isPending ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConfirmDialog({
  drug,
  onClose,
  onConfirm,
  isPending,
}: {
  drug: MedicalDrug;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.confirmDelete")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t("drugs.deleteConfirm", { name: drug.name })}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? t("common.saving") : t("common.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DrugDatabase() {
  const { t } = useTranslation();
  const { data: drugs, isLoading } = useMedicalDrugs();
  const createMutation = useCreateMedicalDrug();
  const updateMutation = useUpdateMedicalDrug();
  const deleteMutation = useDeleteMedicalDrug();

  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MedicalDrug | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MedicalDrug | null>(null);

  const filtered = (drugs ?? []).filter((d) => {
    const q = search.toLowerCase();
    return (
      d.name.toLowerCase().includes(q) ||
      (d.nameKz ?? "").toLowerCase().includes(q) ||
      (d.inn ?? "").toLowerCase().includes(q) ||
      (d.manufacturer ?? "").toLowerCase().includes(q)
    );
  });

  const handleCreate = (form: DrugForm) => {
    createMutation.mutate(
      { name: form.name, nameKz: form.nameKz || undefined, inn: form.inn, manufacturer: form.manufacturer || undefined },
      { onSuccess: () => setAddOpen(false) },
    );
  };

  const handleUpdate = (form: DrugForm) => {
    if (!editTarget) return;
    updateMutation.mutate(
      { id: editTarget.id, name: form.name, nameKz: form.nameKz || undefined, inn: form.inn || undefined, manufacturer: form.manufacturer || undefined },
      { onSuccess: () => setEditTarget(null) },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  };

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("drugs.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("drugs.subtitle")}</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="shrink-0">
          <Plus className="h-4 w-4 mr-2" />
          {t("drugs.addDrug")}
        </Button>
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder={t("common.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">{search ? t("common.noResults") : t("drugs.empty")}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium">{t("drugs.name")}</th>
                <th className="text-left px-4 py-3 font-medium">{t("drugs.nameKz")}</th>
                <th className="text-left px-4 py-3 font-medium">{t("drugs.inn")}</th>
                <th className="text-left px-4 py-3 font-medium">{t("drugs.manufacturer")}</th>
                <th className="w-20" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((drug) => (
                <tr key={drug.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-medium">{drug.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{drug.nameKz ?? "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{drug.inn ?? "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{drug.manufacturer ?? "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => setEditTarget(drug)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(drug)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DrugFormDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleCreate}
        isPending={createMutation.isPending}
      />

      {editTarget && (
        <DrugFormDialog
          open
          onClose={() => setEditTarget(null)}
          initial={{ name: editTarget.name, nameKz: editTarget.nameKz ?? "", inn: editTarget.inn ?? "", manufacturer: editTarget.manufacturer ?? "" }}
          onSubmit={handleUpdate}
          isPending={updateMutation.isPending}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          drug={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isPending={deleteMutation.isPending}
        />
      )}
    </Layout>
  );
}
